import { Card, CardString, Hand, Value } from 'pokersolver'
import {
  getCardsValues,
  FACE_CARDS,
  FACE_CARD_VALUES,
  getCardsSuits,
  sortValues,
  getSortedSpecialSuitedConnectorsValues
} from '../BotPlayerKind.utils'
import { Suits, Values } from '../../Deck'

/**
 * @param cards array of cards
 * @returns true if the cards have a pair
 * @example
 * hasAPair(['8d', '8h']) // true
 * hasAPair(['2d', '2s']) // true
 * hasAPair(['Qd', 'Qc']) // true
 * hasAPair(['8d', 'Td']) // false
 */
export const hasAPair =
  () =>
  (cards: CardString[]): boolean => {
    const solvedHand = Hand.solve(cards)
    return solvedHand.name === 'Pair'
  }

export const hasATopPair =
  () =>
  (cards: CardString[]): boolean => {
    const solvedHand = Hand.solve(cards)
    if (!(solvedHand.name === 'Pair')) {
      return false
    }
    return solvedHand.cards.every((c) => c.rank === 13 || c.rank === 12)
  }

export const hasTwoPair =
  () =>
  (cards: CardString[]): boolean => {
    const solvedHand = Hand.solve(cards)
    return solvedHand.name === 'Two Pair'
  }

export const hasThreeOfKind =
  () =>
  (cards: CardString[]): boolean => {
    const solvedHand = Hand.solve(cards)
    return solvedHand.name === 'Three of a Kind'
  }

export const hasFourOfKind =
  () =>
  (cards: CardString[]): boolean => {
    const solvedHand = Hand.solve(cards)
    return solvedHand.name === 'Four of a Kind'
  }

/**
 * @param cards array of cards
 * @returns true if the cards have a face cards
 * @example
 * hasFaceCards(['Jd', 'Qh']) // true
 * hasFaceCards(['Td', 'Js']) // true
 * hasFaceCards(['Qd', 'Kd']) // true
 * hasFaceCards(['8d', 'Td']) // false
 * */
export const hasFaceCards =
  () =>
  (cards: CardString[]): boolean => {
    const cardValues = getCardsValues(cards)
    return cardValues.every((value) => FACE_CARDS.includes(value as FACE_CARD_VALUES))
  }

/**
 * @param cards array of cards
 * @returns true if the cards have a suited ace
 * @example
 * hasAceSuited(['Ad', '5d']) // true
 * hasAceSuited(['Ad', '5h']) // false
 *  */
export const hasAceSuited =
  () =>
  (cards: CardString[]): boolean => {
    const cardValues = getCardsValues(cards)
    const cardSuits = getCardsSuits(cards)
    return cardValues.includes(FACE_CARD_VALUES.A) && cardSuits.every((suit) => suit === cardSuits[0])
  }

/**
 * @param cards array of cards
 * @returns true if the cards have a suited connectors
 * @example
 * hasSuitedConnectors(['9d', 'Td']) // true
 * hasSuitedConnectors(['2d', '3d']) // true
 * hasSuitedConnectors(['Qd', 'Kd']) // true
 * hasSuitedConnectors(['8d', 'Td']) // false
 */
export const hasSuitedConnectors =
  () =>
  (cards: CardString[]): boolean => {
    const cardValues = getCardsValues(cards)
    const cardSuits = getCardsSuits(cards)
    const cardSuitsSet = new Set(cardSuits)

    // if there are 2 different suits, it's not a suited connector
    if (cardSuitsSet.size !== 1) return false

    const cardValuesArraySorted = sortValues(cardValues)
    const cardValuesArraySortedString = cardValuesArraySorted.join('')
    const specialSuitedConnectorsSorted = getSortedSpecialSuitedConnectorsValues()
    const specialSuitedConnectorsValues = specialSuitedConnectorsSorted.map((value) => value.join(''))
    const valuesAreNextToEachOther = Math.abs(parseInt(cardValues[0]) - parseInt(cardValues[1])) === 1
    return specialSuitedConnectorsValues.includes(cardValuesArraySortedString) || valuesAreNextToEachOther
  }

/**
 * @param cards array of cards
 * @returns true if the cards have a flush draw
 * @example
 * hasFlushDraw(['9d', 'Td', 'Ad', '2d', '5s']) // true
 * hasFlushDraw(['9s', 'Th', 'As', '2s', '5s']) // true
 * hasFlushDraw(['Td', 'Qd', 'Ad', '2s', '5d']) // true
 * hasFlushDraw(['8d', 'Td', 'Ad', '3s', '4h']) // false
 */
export const hasFlushDraw =
  () =>
  (cards: CardString[]): boolean => {
    const cardSuits = getCardsSuits(cards)

    return Suits.some((suit) => cardSuits.filter((cardSuit) => cardSuit === suit).length === 4)
  }

/**
 * @param cards array of cards
 * @returns true if the cards have a flush draw
 * @example
 * hasStraightDraw(['3d', '4s', '5h', '6d']) // true
 * hasStraightDraw(['2d', '4s', '5h', '6d']) // true
 * hasStraightDraw(['6d', '7d', '8d', 'Td']) // true
 * hasStraightDraw(['Ts','8s', 'Ah', '3h'] // false
 * hasStraightDraw(['8d', 'Td', 'As', '3s']) // false
 */
export const hasStraightDraw =
  () =>
  (cards: CardString[]): boolean => {
    let canMakeStraight = false
    Values.forEach((value) => {
      Suits.forEach((suit) => {
        const card = new Card(`${value}${suit}`)
        const newCards = [...cards, card.toString()]
        try {
          const solvedHand = Hand.solve(newCards)
          if (solvedHand.name === 'Straight') {
            canMakeStraight = true
          }
        } catch (e) {
          /* empty */
        }
      })
    })

    return canMakeStraight
  }

export const hasHighCard =
  () =>
  (cards: CardString[]): boolean => {
    try {
      const solvedHand = Hand.solve(cards)
      console.log('LOG: > hasHighCard > solvedHand:', solvedHand)
      return solvedHand.name === 'High Card'
    } catch (e) {
      return false
    }
  }

export const hasPairOfCard =
  (ofValue: string) =>
  (cards: CardString[]): boolean => {
    try {
      //Value
      const solvedHand = Hand.solve(cards)
      const isPair = solvedHand.name === 'Pair'
      if (!isPair) {
        return false
      }
      const cardValues = getCardsValues(cards)

      return isPair && cardValues.includes(ofValue as Value)
    } catch (e) {
      return false
    }
  }

export const hasValueOfCards =
  (ofValue: Value[]) =>
  (cards: CardString[]): boolean => {
    try {
      const cardValues = getCardsValues(cards)
      return cardValues.every((c) => ofValue.includes(c))
    } catch (e) {
      return false
    }
  }

/**
 * @name Two High Cards
 * @description Two High Cards (any combination of 10, J, Q, K, A) ex. Q of hearts 10 of spades or J of clubs and K of diamonds
 * @returns boolean
 */
export const hasTwoHightCards =
  () =>
  (cards: CardString[]): boolean => {
    const solvedHand = Hand.solve(cards)
    return solvedHand.cards.every((c) => c.rank >= 9)
  }
